import React, { useState } from 'react';
import API from '../../../utils/API';
import makeID from '../../../utils/makeID';
import { getOriginalFileURLFromCroppedURL } from '../../../modules/file/file.utils';

import ImageDropperCropper from '../../../pages/nonprofitProfile/ImageDropperCropper/ImageDropperCropper';
import { useDispatch, useSelector } from 'react-redux';
import { saveUser } from '../../../redux/actions';
import Modal from 'react-bootstrap/Modal';
import Form from 'react-bootstrap/Form';
import Button from 'react-bootstrap/Button';
import './index.css';
import UserPreferences from './UserPreferences';
import { IOnImageChangeEvent, IOnImageDeleteEvent } from '../../../types/shared.types';


export interface IUserPreferences {
  ShareMyPrivateInfo: {
    name: boolean;
    gender: boolean;
    age: boolean;
    email: boolean;
    telephone: boolean;
    location: boolean;
  };
  demographic: { gender: string; age: number };
  address: { city: string; state: string };
  telephone: string;
  otherGender: string;
}

interface IDonorProfile {
  show: boolean;
  hide: () => void;
}

const DonorProfile: React.FC<IDonorProfile> = (props: any) => {
  const dispatch = useDispatch();
  const reduxUser = useSelector((state: any) => state.signin);

  const [formState, setFormState] = useState({
    firstName: reduxUser.firstName,
    lastName: reduxUser.lastName,
    organization_company: reduxUser.organization_company,
    profileImg: reduxUser.profileImg,
    _id: reduxUser._id
  });
  const [userPreferences, setUserPreferences] = useState<IUserPreferences>();
  const [UserValidations, setUserValidation] = useState(true);
  const [loading, setLoading] = useState(false);
  const [newImageUpload, setNewImageUpload] = useState<IOnImageChangeEvent>();
  const [imageDelete, setImageDelete] = useState<IOnImageDeleteEvent>();

  const handleChange = (e: any) => {
    setFormState({ ...formState, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e: React.SyntheticEvent) => {
    e.preventDefault();
    if (loading || UserValidations === false) return;
    setLoading(true)
    let newFormState: any = { ...formState };

    if (userPreferences !== undefined) {
      newFormState = {
        ...newFormState,
        ShareMyPrivateInfo: userPreferences.ShareMyPrivateInfo,
        demographic: userPreferences.demographic,
        address: userPreferences.address,
        telephone: userPreferences.telephone
      };
    }

    // Upload new images (cropped + original) if changed
    if (newImageUpload !== undefined) {
      const uploadRes = await API.uploadFile(
        newImageUpload.croppedImage,
        newImageUpload.croppedImageFileName,
        formState.profileImg || undefined
      ).catch((err: any) => { console.error(err); return null; });

      // The S3 key is stable (overwritten in place), so add a cache-busting
      // param — otherwise the navbar keeps showing the old cached image until a
      // hard refresh.
      if (uploadRes?.data?.data?.url) {
        newFormState.profileImg = `${uploadRes.data.data.url}?v=${Date.now()}`;
      }

      if (newImageUpload.originalImage && newImageUpload.originalImageFileName) {
        await API.uploadFile(
          newImageUpload.originalImage,
          newImageUpload.originalImageFileName,
          getOriginalFileURLFromCroppedURL(formState.profileImg) || undefined
        ).catch(console.error);
      }
    }

    // Delete image if requested
    if (imageDelete) {
      if (formState.profileImg) {
        await Promise.all([
          API.deleteFile(formState.profileImg).catch(console.error),
          API.deleteFile(getOriginalFileURLFromCroppedURL(formState.profileImg)).catch(console.error),
        ]);
      }
      newFormState.profileImg = '';
    }

    const res: any = await API.updateUser(newFormState).catch((err: any) => console.log(err));

    if (res?.status === 200) {
      dispatch(
        saveUser({
          ...reduxUser,
          ...formState,
          profileImg: newFormState.profileImg
        })
      );
      props.hide();
    }
    setLoading(false)
  };

  const handleUserPreferences = (updatedPreferences: IUserPreferences) => {
    setUserPreferences({ ...updatedPreferences });
    setFormState({ ...formState });
  };

  const handleImageChange = async (event: IOnImageChangeEvent) => {
    setNewImageUpload(event);
    setImageDelete(undefined);
  };

  const handleImageDelete = async (event: IOnImageDeleteEvent) => {
    setImageDelete(event);
    setNewImageUpload(undefined);
  };

  const fetchPreferences = () => {
    API.fetchUserPreferences(reduxUser._id)
      .then((response: any) => {
        if (!response) return;
        const newPreferences: IUserPreferences = {
          ShareMyPrivateInfo: response.data.ShareMyPrivateInfo,
          demographic: response.data.demographic,
          address: response.data.address,
          telephone: response.data.telephone,
          otherGender:
            response.data.demographic.gender === 'male' ||
              response.data.demographic.gender === 'female' ||
              response.data.demographic.gender === 'non-binary' ||
              response.data.demographic.gender === 'other'
              ? ''
              : response.data.demographic.gender
        };
        setUserPreferences(newPreferences);
        setFormState({ ...formState });
      })
      .catch(console.log);
  };

  return (
    <Modal
      show={props.show}
      onHide={props.hide}
      onEnter={fetchPreferences}
      className="donor-profile-modal-wrapper"
      centered
    >
      <Modal.Header closeButton className="donor-modal-header">
        <Modal.Title as="h5" className="donor-modal-title">
          Edit my profile
        </Modal.Title>
      </Modal.Header>

      <Modal.Body className="donor-profile-modal">
        <div className="profile-content">
          <label style={{ display: 'flex', fontSize: '14px', fontWeight: 600 }}>Profile image</label>
          <div className="form-fields-section">

            <Form onSubmit={handleSubmit}>
              <div className="row gx-8">
                <div className="col-4">
                  <div className="profile-image-section">
                    <div className="profile-image-container with-pencil">
                      <ImageDropperCropper
                        defaultThumbNail={reduxUser.profileImg}
                        field={'profileImg'}
                        aspect="squareCrop"
                        fileName={`prof-pic-${reduxUser._id + makeID(15)}`}
                        onImageChange={handleImageChange}
                        onImageDelete={handleImageDelete}
                      />
                    </div>
                    {/* <label className="profile-image-label">Profile image</label> */}
                  </div>

                </div>
                <div className="col-8">
                  <div className="d-flex flex-column">
                    <div className="d-flex   justify-content-between">
                      <Form.Group className="form-group-half mr-1">
                        <Form.Control
                          value={formState.firstName}
                          name="firstName"
                          type="text"
                          placeholder="Name"
                          className="form-input-field"
                          onChange={handleChange}
                        />
                      </Form.Group>
                      <Form.Group className="form-group-half">
                        <Form.Control
                          value={formState.lastName}
                          name="lastName"
                          type="text"
                          placeholder="Last Name"
                          className="form-input-field"
                          onChange={handleChange}
                        />
                      </Form.Group>
                    </div>
                    <Form.Group className="form-group-full">
                      <Form.Control
                        value={formState.organization_company}
                        name="organization_company"
                        type="text"
                        placeholder="Company / Organization"
                        className=""
                        onChange={handleChange}
                      />
                    </Form.Group>
                  </div>
                </div>

              </div>



              {/* Preferences heading & helper text (visual only; logic stays in <UserPreferences/>) */}
              <div className="preferences-header">
                <div className="preferences-title">Preferences</div>
                <div className="preferences-description">
                  When you donate the information you check below will be shared with the nonprofits in order to improve their operations.
                </div>
              </div>

              {!userPreferences ? (
                <h5 className="loading-preferences">LOADING PREFERENCES...</h5>
              ) : (
                <UserPreferences
                  preferences={userPreferences}
                  setUserPreferences={handleUserPreferences}
                  userName={`${reduxUser.firstName} ${reduxUser.lastName}`}
                  email={reduxUser.email}
                  setUserValidation={setUserValidation}
                />
              )}

              {/* Footer actions */}
              <div className="modal-actions">
                <Button
                  type="submit"
                  className={`btn-save-modal ${UserValidations === false || loading ? 'btn-save-disabled' : 'btn-save-active'}`}
                  disabled={!UserValidations || loading}
                >
                  {loading ? 'Loading...' : 'Save'}
                </Button>
                <Button
                  type="button"
                  className="btn-save-modal outline-primary"
                  onClick={props.hide}
                >
                  Close
                </Button>
              </div>
            </Form>
          </div>
        </div>
      </Modal.Body>
    </Modal>
  );
};

export default DonorProfile;
